import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  CloudUpload, PhotoCamera, Replay, Close, CheckCircle, ErrorOutline, InsertDriveFile,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import store from '../utilities/store';
import constants from '../utilities/constants';
import EntityLinkPicker from './EntityLinkPicker';
import DocumentScanner from './DocumentScanner';
import { useSelectMenuProps, useAutocompleteSlotProps } from '../utilities/ModalStackContext';

const CONCURRENCY = 5;

// Phone photos / scans are multi-MB; shrink them before upload for throughput
// and storage. Scans stay legible at 2560px. Non-images are uploaded as-is.
const COMPRESS_OPTS = { maxSizeMB: 2, maxWidthOrHeight: 2560, useWebWorker: true };
const shouldCompress = (file) => !!file.type && file.type.startsWith('image/');

const keyOf = (l) => `${l.type}:${l.id}`;
const dedupeLinks = (links) => {
  const seen = new Set();
  return links.filter(l => {
    const k = keyOf(l);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const formatSize = (bytes = 0) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// Reusable mass uploader. Parallel uploads (capped), per-file progress + retry,
// batch metadata (category / tags / entity links), in-app document scanner
// (edge detection + auto-capture + deskew), and client-side image compression.
//
// Props:
//   lockedLinks  – links always applied & shown fixed (e.g. the car this tab belongs to)
//   onUploaded   – called with each created file doc as it finishes
//   compact      – tighter layout for embedding inside a modal tab
export default function FileUploader({ lockedLinks = [], onUploaded, compact = false }) {
  const [category, setCategory] = React.useState('other');
  const [tags, setTags] = React.useState([]);
  const [links, setLinks] = React.useState([]);
  const [queue, setQueue] = React.useState([]);
  const [scanOpen, setScanOpen] = React.useState(false);

  const activeRef = React.useRef(0);
  const startedRef = React.useRef(new Set());
  const keyRef = React.useRef(0);
  // Hidden <input capture> that opens the device camera for a plain photo (vs.
  // the document scanner, which is tuned for deskewing paper).
  const cameraInputRef = React.useRef(null);
  const selectMenuProps = useSelectMenuProps();
  const tagSlotProps = useAutocompleteSlotProps();

  // Mirror current metadata + locked links into refs so the camera handler
  // (a long-lived global callback) always reads the latest values.
  const metaRef = React.useRef({ category, tags, links, lockedLinks });
  React.useEffect(() => {
    metaRef.current = { category, tags, links, lockedLinks };
  }, [category, tags, links, lockedLinks]);

  const updateItem = (key, patch) =>
    setQueue(q => q.map(it => (it.key === key ? { ...it, ...patch } : it)));

  const uploadItem = React.useCallback(async (item) => {
    try {
      let file = item.file;
      if (shouldCompress(file)) {
        updateItem(item.key, { status: 'processing' });
        try {
          file = await imageCompression(file, COMPRESS_OPTS);
        } catch (err) {
          console.warn('Image compression failed, uploading original', err);
          file = item.file;
        }
      }
      updateItem(item.key, { status: 'uploading', progress: 0, error: null });
      const uploaded = await store.uploadLibraryFile(
        file,
        item.meta,
        (p) => updateItem(item.key, { progress: p }),
      );
      updateItem(item.key, { status: 'done', progress: 100 });
      if (onUploaded) onUploaded(uploaded);
      // Kick off AI extraction for bills so fields are ready on the Bills page.
      if (uploaded && item.meta && item.meta.category === 'bill') {
        store.extractBill(uploaded.id);
      }
    } catch (err) {
      console.error(err);
      updateItem(item.key, { status: 'error', error: err?.message || 'Upload failed' });
    } finally {
      activeRef.current -= 1;
      setQueue(q => [...q]); // nudge the pump effect
    }
  }, [onUploaded]);

  // Pump: start pending items up to the concurrency cap. uploadItem owns the
  // visible status; startedRef guards against double-starting an item.
  React.useEffect(() => {
    if (activeRef.current >= CONCURRENCY) return;
    for (const it of queue) {
      if (activeRef.current >= CONCURRENCY) break;
      if (it.status === 'pending' && !startedRef.current.has(it.key)) {
        startedRef.current.add(it.key);
        activeRef.current += 1;
        uploadItem(it);
      }
    }
  }, [queue, uploadItem]);

  const enqueue = React.useCallback((files) => {
    if (!files || !files.length) return;
    const m = metaRef.current;
    const meta = { category: m.category, tags: m.tags, links: dedupeLinks([...m.lockedLinks, ...m.links]) };
    const items = files.map(file => ({
      key: `f${keyRef.current++}`,
      file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      error: null,
      meta,
    }));
    setQueue(q => [...q, ...items]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ multiple: true, onDrop: enqueue });

  const retry = (key) => {
    startedRef.current.delete(key);
    updateItem(key, { status: 'pending', error: null, progress: 0 });
  };

  const remove = (key) => {
    startedRef.current.delete(key);
    setQueue(q => q.filter(it => it.key !== key));
  };

  const clearFinished = () => setQueue(q => q.filter(it => it.status !== 'done'));

  const counts = React.useMemo(() => ({
    total: queue.length,
    done: queue.filter(it => it.status === 'done').length,
    active: queue.filter(it => it.status === 'uploading' || it.status === 'processing').length,
    error: queue.filter(it => it.status === 'error').length,
  }), [queue]);

  const dropzoneStyle = {
    border: '2px dashed',
    borderColor: isDragActive ? 'primary.main' : 'grey.400',
    borderRadius: 2,
    p: compact ? 2.5 : 4,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  return (
    <Box>
      {/* Batch metadata applied to the next files added */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="file-category-label">Category</InputLabel>
            <Select
              labelId="file-category-label"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              MenuProps={selectMenuProps}
            >
              {constants.fileCategories.map(c => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8} md={4}>
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={tags}
            onChange={(_, v) => setTags(v)}
            slotProps={tagSlotProps}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" variant="outlined" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder={tags.length ? undefined : 'Add tags…'} />
            )}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <EntityLinkPicker value={links} onChange={setLinks} />
          {lockedLinks.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              {lockedLinks.map(l => (
                <Chip key={keyOf(l)} size="small" color="primary" label={l.label} />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>

      <Box {...getRootProps()} sx={dropzoneStyle}>
        <input {...getInputProps()} />
        <Stack alignItems="center" spacing={1}>
          <CloudUpload sx={{ fontSize: compact ? 32 : 40, color: 'text.secondary' }} />
          <Typography variant={compact ? 'body2' : 'body1'}>
            {isDragActive ? 'Drop the files here…' : 'Drag & drop files here, or click to select'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            <Button
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={(e) => { e.stopPropagation(); cameraInputRef.current && cameraInputRef.current.click(); }}
            >
              Take photo
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={(e) => { e.stopPropagation(); setScanOpen(true); }}
            >
              Scan documents
            </Button>
          </Stack>
          {/* Native camera capture for a normal photo. capture="environment"
              opens the rear camera on mobile; on desktop it's a plain image
              picker. Goes through the same compress + upload pipeline. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            style={{ display: 'none' }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              enqueue(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Auto-detects & crops each page · stays open for back-to-back scans · uploaded {CONCURRENCY} at a time
          </Typography>
        </Stack>
      </Box>

      {queue.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {counts.done}/{counts.total} uploaded
              {counts.error ? ` · ${counts.error} failed` : ''}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {counts.done > 0 && (
              <Button size="small" onClick={clearFinished}>Clear finished</Button>
            )}
          </Box>
          <Paper variant="outlined">
            {queue.map((it, idx) => (
              <Box
                key={it.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderBottom: idx === queue.length - 1 ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                {it.status === 'done' ? <CheckCircle color="success" />
                  : it.status === 'error' ? <ErrorOutline color="error" />
                  : <InsertDriveFile sx={{ color: 'text.secondary' }} />}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap title={it.name}>{it.name}</Typography>
                  {it.status === 'uploading' ? (
                    <LinearProgress variant="determinate" value={it.progress} sx={{ mt: 0.5 }} />
                  ) : it.status === 'processing' ? (
                    <Box sx={{ mt: 0.5 }}>
                      <LinearProgress />
                      <Typography variant="caption" color="text.secondary">Optimizing…</Typography>
                    </Box>
                  ) : it.status === 'error' ? (
                    <Typography variant="caption" color="error">{it.error}</Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {it.status === 'done' ? 'Done' : 'Queued'}{it.size ? ` · ${formatSize(it.size)}` : ''}
                    </Typography>
                  )}
                </Box>
                {it.status === 'error' && (
                  <Tooltip title="Retry">
                    <IconButton size="small" onClick={() => retry(it.key)}><Replay fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                {it.status !== 'uploading' && it.status !== 'processing' && (
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => remove(it.key)}><Close fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      <DocumentScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onCapture={(file) => enqueue([file])}
      />
    </Box>
  );
}
